/**
 * Country picker for the economic outlook page.
 *
 * This feature is a flat list of ISO codes (no bank hierarchy), so we render
 * a simple button row and fall back to a native `<select>` on mobile.
 */
import type { CountryConfig, CountryIso } from '../types';
import { DEFAULT_COUNTRY_ORDER, longName } from '../lib/countries';

export interface CountrySelectorProps {
  value: CountryIso;
  onChange: (country: CountryIso) => void;
  countries?: Record<CountryIso, CountryConfig> | null;
}

function sortedIsos(countries: Record<CountryIso, CountryConfig> | null | undefined): CountryIso[] {
  if (!countries) return DEFAULT_COUNTRY_ORDER;
  return Object.keys(countries).sort(
    (a, b) => (countries[a]?.display_order ?? 0) - (countries[b]?.display_order ?? 0),
  );
}

function labelFor(
  iso: CountryIso,
  countries: Record<CountryIso, CountryConfig> | null | undefined,
): string {
  return countries?.[iso]?.display_name ?? longName(iso);
}

export default function CountrySelector({
  value,
  onChange,
  countries,
}: CountrySelectorProps): JSX.Element {
  const isos = sortedIsos(countries);

  return (
    <div className="flex items-center gap-4" role="radiogroup" aria-label="Country">
      <label className="whitespace-nowrap font-[var(--font-heading)] text-sm font-semibold text-gray-700">
        Country:
      </label>
      <div className="hidden overflow-hidden rounded-lg border border-gray-200 shadow-sm md:flex">
        {isos.map((iso) => {
          const active = iso === value;
          return (
            <button
              key={iso}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(iso)}
              className={`min-w-[70px] flex-1 border-r border-gray-200 px-3 py-2 text-xs font-medium transition-all duration-200 last:border-r-0 ${
                active
                  ? 'bg-[color:var(--accent)] font-semibold text-white shadow-[inset_0_1px_3px_rgba(0,0,0,0.1)]'
                  : 'bg-white text-gray-500 hover:-translate-y-0.5 hover:bg-gray-50 hover:text-gray-700'
              }`}
            >
              {labelFor(iso, countries)}
            </button>
          );
        })}
      </div>
      <select
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-[color:var(--accent)] md:hidden"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Country"
      >
        {isos.map((iso) => (
          <option key={iso} value={iso}>
            {labelFor(iso, countries)}
          </option>
        ))}
      </select>
    </div>
  );
}
